import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Tag, Button, Space, Row, Col, Table, Input, message, Select,
    Typography, Progress, Spin, Avatar, Empty, Tabs, Collapse
} from 'antd';
import {
    ArrowLeftOutlined,
    ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
    SearchOutlined,
    CrownOutlined, CarOutlined, TeamOutlined, EyeOutlined,
    TrophyOutlined, PhoneOutlined, DownOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Configuration des paliers avec seuils et montants dynamiques par ville
const getAffiliateMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'account_creation', rides: 0, amount: aff.accountCreation || 500, label: 'Compte' },
        { key: 'activation_rides_5', rides: t.milestoneThreshold1 || 5, amount: aff.rides5 || 900, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', rides: t.milestoneThreshold2 || 15, amount: aff.rides15 || 1350, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', rides: t.milestoneThreshold3 || 35, amount: aff.rides35 || 1800, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', rides: t.milestoneThreshold4 || 55, amount: aff.rides55 || 1800, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const getAffiliateReactivationMilestones = (r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'account_creation', rides: 0, amount: aff.accountCreation || 500, label: 'Compte' },
        { key: 'reactivation_rides_20', rides: 20, amount: aff.reactivationRides20 || 1500, label: '20 courses' },
        { key: 'reactivation_hours_130', rides: 0, hours: 130, amount: aff.reactivationHours130 || 900, label: '130 heures' },
    ];
};

const getAmbassadorMilestones = (t = {}, r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', rides: 0, amount: amb.accountCreation || 50, label: 'Compte' },
        { key: 'activation_rides_5', rides: t.milestoneThreshold1 || 5, amount: amb.rides5 || 100, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', rides: t.milestoneThreshold2 || 15, amount: amb.rides15 || 150, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', rides: t.milestoneThreshold3 || 35, amount: amb.rides35 || 200, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', rides: t.milestoneThreshold4 || 55, amount: amb.rides55 || 200, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const getAmbassadorReactivationMilestones = (r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', rides: 0, amount: amb.accountCreation || 50, label: 'Compte' },
        { key: 'reactivation_rides_20', rides: 20, amount: amb.reactivationRides20 || 150, label: '20 courses' },
        { key: 'reactivation_hours_130', rides: 0, hours: 130, amount: amb.reactivationHours130 || 100, label: '130 heures' },
    ];
};

// Paliers pour chauffeurs directs de l'ambassadeur (affiliate + ambassador)
const getAmbassadorDirectMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', rides: 0, amount: (aff.accountCreation || 500) + (amb.accountCreation || 50), label: 'Compte', affiliateAmount: aff.accountCreation || 500, ambassadorAmount: amb.accountCreation || 50 },
        { key: 'activation_rides_5', rides: t.milestoneThreshold1 || 5, amount: (aff.rides5 || 900) + (amb.rides5 || 100), label: `${t.milestoneThreshold1 || 5} courses`, affiliateAmount: aff.rides5 || 900, ambassadorAmount: amb.rides5 || 100 },
        { key: 'activation_rides_15', rides: t.milestoneThreshold2 || 15, amount: (aff.rides15 || 1350) + (amb.rides15 || 150), label: `${t.milestoneThreshold2 || 15} courses`, affiliateAmount: aff.rides15 || 1350, ambassadorAmount: amb.rides15 || 150 },
        { key: 'activation_rides_35', rides: t.milestoneThreshold3 || 35, amount: (aff.rides35 || 1800) + (amb.rides35 || 200), label: `${t.milestoneThreshold3 || 35} courses`, affiliateAmount: aff.rides35 || 1800, ambassadorAmount: amb.rides35 || 200 },
        { key: 'activation_rides_55', rides: t.milestoneThreshold4 || 55, amount: (aff.rides55 || 1800) + (amb.rides55 || 200), label: `${t.milestoneThreshold4 || 55} courses`, affiliateAmount: aff.rides55 || 1800, ambassadorAmount: amb.rides55 || 200 },
    ];
};

const getAmbassadorDirectReactivationMilestones = (r = {}) => {
    const aff = r.affiliate || {};
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', rides: 0, amount: (aff.accountCreation || 500) + (amb.accountCreation || 50), label: 'Compte', affiliateAmount: aff.accountCreation || 500, ambassadorAmount: amb.accountCreation || 50 },
        { key: 'reactivation_rides_20', rides: 20, amount: (aff.reactivationRides20 || 1500) + (amb.reactivationRides20 || 150), label: '20 courses', affiliateAmount: aff.reactivationRides20 || 1500, ambassadorAmount: amb.reactivationRides20 || 150 },
        { key: 'reactivation_hours_130', rides: 0, hours: 130, amount: (aff.reactivationHours130 || 900) + (amb.reactivationHours130 || 100), label: '130 heures', affiliateAmount: aff.reactivationHours130 || 900, ambassadorAmount: amb.reactivationHours130 || 100 },
    ];
};

const CommissionDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [commission, setCommission] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [milestoneThresholds, setMilestoneThresholds] = useState({});
    const [commissionRates, setCommissionRates] = useState({});
    const [directDrivers, setDirectDrivers] = useState([]);
    const [affiliatesWithDrivers, setAffiliatesWithDrivers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedCity, setSelectedCity] = useState(null);
    const [activeTab, setActiveTab] = useState('direct');

    const fetchCommission = async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.getCommissionById(id);
            if (response.success) {
                const {
                    drivers: driversList,
                    directDrivers: direct,
                    affiliatesWithDrivers: affiliates,
                    summary: sum,
                    ...commissionData
                } = response.data;
                setCommission(commissionData);
                setDrivers(driversList || []);
                setDirectDrivers(direct || []);
                setAffiliatesWithDrivers(affiliates || []);
                setSummary(sum || null);
                if (response.data.milestoneThresholds) {
                    setMilestoneThresholds(response.data.milestoneThresholds);
                }
                if (response.data.commissionRates) {
                    setCommissionRates(response.data.commissionRates);
                }
            } else {
                message.error('Commission non trouvée');
                navigate('/dashboard/affiliate-commissions');
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
            navigate('/dashboard/affiliate-commissions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCommission(); }, [id]);


    const getStatusConfig = (status) => {
        const configs = {
            pending: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', text: 'En attente', icon: <ClockCircleOutlined /> },
            pending_validation: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', text: 'Validation', icon: <ClockCircleOutlined /> },
            approved: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', text: 'Approuvée', icon: <CheckCircleOutlined /> },
            paid: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', text: 'Payée', icon: <CheckCircleOutlined /> },
            cancelled: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7', text: 'Annulée', icon: <CloseCircleOutlined /> }
        };
        return configs[status] || configs.pending;
    };

    const getBeneficiary = (record) => {
        if (record.recipientType === 'ambassador' && record.ambassadorId) {
            return {
                name: `${record.ambassadorId.firstName || ''} ${record.ambassadorId.lastName || ''}`.trim() || 'Ambassadeur',
                phone: record.ambassadorId.phone,
                type: 'ambassador'
            };
        }
        if (record.affiliateId) {
            return {
                name: `${record.affiliateId.firstName || ''} ${record.affiliateId.lastName || ''}`.trim() || 'Affilié',
                phone: record.affiliateId.phone,
                type: 'affiliate'
            };
        }
        return { name: '-', phone: '', type: 'unknown' };
    };

    // Vérifier si des chauffeurs sont en réactivation
    const hasReactivationDrivers = () => {
        const allDriversList = [...drivers, ...directDrivers];
        return allDriversList.some(d => d.driverType === 'reactivation');
    };

    const hasNewDrivers = () => {
        const allDriversList = [...drivers, ...directDrivers];
        return allDriversList.some(d => d.driverType !== 'reactivation');
    };

    const getMilestones = (isDirect = false, isReactivation = false, thresholds = baremeThresholds, rates = baremeRates) => {
        if (commission?.recipientType === 'ambassador') {
            if (isDirect) {
                return isReactivation ? getAmbassadorDirectReactivationMilestones(rates) : getAmbassadorDirectMilestones(thresholds, rates);
            }
            return isReactivation ? getAmbassadorReactivationMilestones(rates) : getAmbassadorMilestones(thresholds, rates);
        }
        return isReactivation ? getAffiliateReactivationMilestones(rates) : getAffiliateMilestones(thresholds, rates);
    };
    const getTotalPotential = (isDirect = false, isReactivation = false) => getMilestones(isDirect, isReactivation).reduce((sum, m) => sum + m.amount, 0);
    const getTotalPotentialDirect = (isReactivation = false) => (isReactivation ? getAmbassadorDirectReactivationMilestones(baremeRates) : getAmbassadorDirectMilestones(baremeThresholds, baremeRates)).reduce((sum, m) => sum + m.amount, 0);
    const getTotalPotentialIndirect = (isReactivation = false) => (isReactivation ? getAmbassadorReactivationMilestones(baremeRates) : getAmbassadorMilestones(baremeThresholds, baremeRates)).reduce((sum, m) => sum + m.amount, 0);

    const renderMilestoneTag = (achieved, label, currentRides = null, targetRides = null) => {
        // Afficher la progression si on a le nombre de courses
        let displayLabel = label;
        let progressColor = '#999';

        if (targetRides !== null && currentRides !== null && !achieved) {
            // Pour les paliers de courses, montrer la progression
            progressColor = currentRides >= targetRides ? '#52c41a' : '#1890ff';
        }

        return (
            <Tag
                style={{
                    margin: '2px',
                    borderRadius: '12px',
                    border: achieved ? '1px solid #52c41a' : '1px solid #d9d9d9',
                    background: achieved ? '#f6ffed' : '#fafafa',
                    color: achieved ? '#52c41a' : progressColor
                }}
                icon={achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            >
                {displayLabel}
            </Tag>
        );
    };

    // Colonnes pour chauffeurs directs (avec montants combinés)
    const getDriverColumns = (isDirect = false) => [
        {
            title: 'Chauffeur',
            key: 'name',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        style={{
                            background: record.isDirect ? '#f9f0ff' : '#fff7e6',
                            color: record.isDirect ? '#722ed1' : '#fa8c16'
                        }}
                        icon={<CarOutlined />}
                    />
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626' }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.phone}</div>
                        {record.isDirect && (
                            <Tag color="purple" style={{ marginTop: 4, fontSize: 10, borderRadius: 8 }}>
                                Direct
                            </Tag>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            width: 100,
            render: (_, record) => {
                const cityName = record.city?.name || '-';
                return (
                    <Tag style={{ margin: 0, fontSize: 11 }}>{cityName}</Tag>
                );
            }
        },
        {
            title: 'Progression',
            key: 'milestones',
            responsive: ['md'],
            render: (_, record) => {
                const m = record.milestones || {};
                const currentRides = record.currentStats?.totalRides || 0;
                const isReactivation = record.driverType === 'reactivation';

                return (
                    <div>
                        {/* Afficher le nombre de courses actuelles */}
                        <div style={{
                            marginBottom: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#1890ff'
                        }}>
                            <CarOutlined style={{ marginRight: 4 }} />
                            {currentRides} course{currentRides > 1 ? 's' : ''}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {renderMilestoneTag(m.accountCreation, 'Compte')}
                            {isReactivation ? (
                                // Paliers réactivation: seulement 20 courses
                                renderMilestoneTag(m.rides20Reactivation, currentRides >= 20 ? '20' : `${currentRides}/20`, currentRides, 20)
                            ) : (
                                // Paliers nouveau chauffeur: seuils dynamiques par ville
                                (() => {
                                    const t = record.milestoneThresholds || milestoneThresholds;
                                    const t1 = t.milestoneThreshold1 || 5;
                                    const t2 = t.milestoneThreshold2 || 15;
                                    const t3 = t.milestoneThreshold3 || 35;
                                    const t4 = t.milestoneThreshold4 || 55;
                                    return <>
                                        {renderMilestoneTag(m.rides5, currentRides >= t1 ? `${t1}` : `${currentRides}/${t1}`, currentRides, t1)}
                                        {renderMilestoneTag(m.rides15, currentRides >= t2 ? `${t2}` : `${currentRides}/${t2}`, currentRides, t2)}
                                        {renderMilestoneTag(m.rides35, currentRides >= t3 ? `${t3}` : `${currentRides}/${t3}`, currentRides, t3)}
                                        {renderMilestoneTag(m.rides55, currentRides >= t4 ? `${t4}` : `${currentRides}/${t4}`, currentRides, t4)}
                                    </>;
                                })()
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Gains',
            key: 'revenue',
            width: 100,
            render: (_, record) => (
                <span style={{ fontWeight: 700, color: record.isDirect ? '#722ed1' : '#52c41a', fontSize: 14 }}>
                    {(record.totalRevenue || 0).toLocaleString()} F
                </span>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/dashboard/affiliate-commissions/${id}/driver/${record._id}?type=${commission?.recipientType || 'affiliate'}&isDirect=${record.isDirect ? 'true' : 'false'}`)}
                    style={{ color: '#1890ff' }}
                />
            )
        }
    ];

    const driverColumns = getDriverColumns();

    // Extraire les villes uniques de tous les chauffeurs
    const allDriversList = [...drivers, ...directDrivers, ...(affiliatesWithDrivers || []).flatMap(a => a.drivers || [])];
    const cityOptions = [...new Map(
        allDriversList
            .filter(d => d.city?.name)
            .map(d => [d.city.name, { label: d.city.name, value: d.city.name }])
    ).values()].sort((a, b) => a.label.localeCompare(b.label));

    // Seuils et montants du barème en fonction de la ville sélectionnée
    const getBaremeThresholds = () => {
        if (!selectedCity) return milestoneThresholds;
        const driverOfCity = allDriversList.find(d => d.city?.name === selectedCity && d.milestoneThresholds);
        return driverOfCity?.milestoneThresholds || milestoneThresholds;
    };
    const baremeThresholds = getBaremeThresholds();

    const getBaremeRates = () => {
        if (!selectedCity) return commissionRates;
        const driverOfCity = allDriversList.find(d => d.city?.name === selectedCity && d.commissionRates);
        return driverOfCity?.commissionRates || commissionRates;
    };
    const baremeRates = getBaremeRates();

    const filterDrivers = (driversList) => {
        let filtered = driversList;
        if (selectedCity) {
            filtered = filtered.filter(d => d.city?.name === selectedCity);
        }
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(d =>
                (d.firstName?.toLowerCase() || '').includes(search) ||
                (d.lastName?.toLowerCase() || '').includes(search) ||
                (d.phone || '').includes(search)
            );
        }
        return filtered;
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!commission) {
        return <Empty description="Commission non trouvée" />;
    }

    const beneficiary = getBeneficiary(commission);
    const statusConfig = getStatusConfig(commission.status);
    const isAmbassador = beneficiary.type === 'ambassador';
    const themeColor = isAmbassador ? '#722ed1' : '#1890ff';

    // Déterminer le type de barème basé sur les chauffeurs
    const showReactivationBareme = hasReactivationDrivers() && !hasNewDrivers();
    const showBothBaremes = hasReactivationDrivers() && hasNewDrivers();
    const milestones = getMilestones(false, showReactivationBareme);
    const totalPotential = getTotalPotential(false, showReactivationBareme);

    // Calculer les stats
    const totalDrivers = isAmbassador ? (summary?.directDriversCount || 0) + (summary?.indirectDriversCount || 0) : drivers.length;
    const totalRevenue = isAmbassador ? (summary?.directRevenue || 0) + (summary?.indirectRevenue || 0) : drivers.reduce((sum, d) => sum + (d.totalRevenue || 0), 0);
    // Chauffeur "complété" = tous les paliers atteints (5 pour nouveau, 2 pour réactivation)
    const completedDrivers = drivers.filter(d => {
        const requiredMilestones = d.driverType === 'reactivation' ? 2 : 5;
        return d.milestonesCount >= requiredMilestones;
    }).length;
    const progressPercent = totalDrivers > 0 ? Math.round((completedDrivers / totalDrivers) * 100) : 0;

    // Render pour affilié (vue simple)
    const renderAffiliateView = () => (
        <Card
            style={{ borderRadius: 12, height: '100%' }}
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                        Chauffeurs recrutés
                        <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{filterDrivers(drivers).length}</Tag>
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {cityOptions.length >= 1 && (
                            <Select
                                placeholder="Toutes les villes"
                                value={selectedCity}
                                onChange={(value) => setSelectedCity(value)}
                                allowClear
                                style={{ minWidth: 140, borderRadius: 8 }}
                                options={cityOptions}
                            />
                        )}
                        <Input
                            placeholder="Rechercher..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 220, borderRadius: 8 }}
                            allowClear
                        />
                    </div>
                </div>
            }
            bodyStyle={{ padding: 0 }}
        >
            <Table
                columns={driverColumns}
                dataSource={filterDrivers(drivers)}
                rowKey="_id"
                pagination={{ pageSize: 20, showSizeChanger: false, size: 'small', style: { marginRight: 16 } }}
                locale={{ emptyText: <Empty description="Aucun chauffeur recruté" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                size="middle"
            />
        </Card>
    );

    // Render pour ambassadeur (vue avec onglets)
    const renderAmbassadorView = () => (
        <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 0 }}>
            {/* Résumé ambassadeur */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', background: '#f9f0ff' }}>
                <Row gutter={16}>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>{summary?.directDriversCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Chauffeurs directs</div>
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{summary?.affiliatesCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Affiliés</div>
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{summary?.indirectDriversCount || 0}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Via affiliés</div>
                    </Col>
                </Row>
            </div>

            {/* Recherche et filtre ville */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {cityOptions.length >= 1 && (
                    <Select
                        placeholder="Toutes les villes"
                        value={selectedCity}
                        onChange={(value) => setSelectedCity(value)}
                        allowClear
                        style={{ minWidth: 140 }}
                        options={cityOptions}
                    />
                )}
                <Input
                    placeholder="Rechercher un chauffeur..."
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ borderRadius: 8, flex: 1 }}
                    allowClear
                />
            </div>

            {/* Onglets */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                style={{ padding: '0 20px' }}
                items={[
                    {
                        key: 'direct',
                        label: (
                            <span>
                                <CarOutlined style={{ marginRight: 6 }} />
                                Chauffeurs directs
                                <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{directDrivers.length}</Tag>
                            </span>
                        ),
                        children: (
                            <Table
                                columns={driverColumns}
                                dataSource={filterDrivers(directDrivers)}
                                rowKey="_id"
                                pagination={{ pageSize: 20, showSizeChanger: false, size: 'small' }}
                                locale={{ emptyText: <Empty description="Aucun chauffeur recruté directement" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                                size="small"
                            />
                        )
                    },
                    {
                        key: 'affiliates',
                        label: (
                            <span>
                                <TeamOutlined style={{ marginRight: 6 }} />
                                Via affiliés
                                <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{summary?.indirectDriversCount || 0}</Tag>
                            </span>
                        ),
                        children: affiliatesWithDrivers.length > 0 ? (
                            <Collapse
                                bordered={false}
                                expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                                style={{ background: 'transparent' }}
                            >
                                {affiliatesWithDrivers.map(affiliate => (
                                    <Panel
                                        key={affiliate._id}
                                        header={
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Avatar size="small" style={{ background: '#1890ff' }} icon={<TeamOutlined />} />
                                                    <div>
                                                        <span style={{ fontWeight: 600 }}>{affiliate.firstName} {affiliate.lastName}</span>
                                                        <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c' }}>{affiliate.phone}</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                    <Tag color="blue">{affiliate.driversCount} chauffeur{affiliate.driversCount > 1 ? 's' : ''}</Tag>
                                                    <span style={{ fontWeight: 700, color: '#52c41a' }}>{(affiliate.totalRevenue || 0).toLocaleString()} F</span>
                                                </div>
                                            </div>
                                        }
                                        style={{ marginBottom: 8, background: '#fafafa', borderRadius: 8 }}
                                    >
                                        <Table
                                            columns={driverColumns}
                                            dataSource={filterDrivers(affiliate.drivers || [])}
                                            rowKey="_id"
                                            pagination={false}
                                            size="small"
                                        />
                                    </Panel>
                                ))}
                            </Collapse>
                        ) : (
                            <Empty description="Aucun affilié avec des chauffeurs" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )
                    }
                ]}
            />
        </Card>
    );

    return (
        <div style={{ padding: '16px', maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
                gap: 12
            }}>
                <Space size="middle">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/affiliate-commissions')}
                        style={{ borderRadius: 8 }}
                    />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#262626' }}>
                            Détails Commission
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {dayjs(commission.createdAt).format('DD MMM YYYY à HH:mm')}
                        </Text>
                    </div>
                </Space>
                <div style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    background: statusConfig.bg,
                    border: `1px solid ${statusConfig.border}`,
                    color: statusConfig.color,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    {statusConfig.icon}
                    {statusConfig.text}
                </div>
            </div>

            <Row gutter={[20, 20]}>
                {/* Colonne gauche */}
                <Col xs={24} lg={8}>
                    {/* Carte Bénéficiaire */}
                    <Card
                        style={{
                            borderRadius: 12,
                            marginBottom: 20,
                            border: `2px solid ${themeColor}20`,
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        <div style={{
                            background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
                            padding: 20,
                            textAlign: 'center'
                        }}>
                            <Avatar
                                size={64}
                                style={{ background: themeColor, marginBottom: 12 }}
                                icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                            />
                            <div style={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>
                                {beneficiary.name}
                            </div>
                            <Tag color={isAmbassador ? 'purple' : 'blue'} style={{ marginTop: 8, borderRadius: 12 }}>
                                {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                            </Tag>
                            <div style={{ marginTop: 12, color: '#8c8c8c', fontSize: 13 }}>
                                <PhoneOutlined style={{ marginRight: 6 }} />
                                {beneficiary.phone || '-'}
                            </div>
                        </div>
                    </Card>

                    {/* Stats Cards */}
                    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <CarOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>{totalDrivers}</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Chauffeurs</div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                <TrophyOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                                <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{completedDrivers}</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>Complétés</div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Revenus */}
                    <Card style={{ borderRadius: 12, marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>Revenus générés</Text>
                            <div style={{ fontSize: 32, fontWeight: 700, color: '#52c41a', margin: '8px 0' }}>
                                {totalRevenue.toLocaleString()} <span style={{ fontSize: 16 }}>FCFA</span>
                            </div>
                            {isAmbassador && summary && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 12, color: '#722ed1' }}>
                                        Direct: {(summary.directRevenue || 0).toLocaleString()} F
                                    </div>
                                    <div style={{ fontSize: 12, color: '#1890ff' }}>
                                        Via affiliés: {(summary.indirectRevenue || 0).toLocaleString()} F
                                    </div>
                                </div>
                            )}
                            <Progress
                                percent={progressPercent}
                                strokeColor={{ '0%': '#52c41a', '100%': '#95de64' }}
                                trailColor="#f0f0f0"
                                strokeWidth={8}
                                style={{ marginTop: 12 }}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {completedDrivers}/{totalDrivers} chauffeurs ont atteint tous les paliers
                            </Text>
                        </div>
                    </Card>

                    {/* Paliers */}
                    {isAmbassador ? (
                        // Pour les ambassadeurs, afficher les deux barèmes
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    <span style={{ fontWeight: 600 }}>Barème des commissions</span>
                                    {cityOptions.length >= 1 && (
                                        <Select
                                            placeholder="Ville"
                                            value={selectedCity}
                                            onChange={(value) => setSelectedCity(value)}
                                            allowClear
                                            size="small"
                                            style={{ minWidth: 120 }}
                                            options={cityOptions}
                                        />
                                    )}
                                </div>
                            }
                            style={{ borderRadius: 12, marginBottom: 20 }}
                            bodyStyle={{ padding: 0 }}
                        >
                            {/* Barème chauffeurs directs */}
                            <div style={{ background: '#f9f0ff', padding: '12px 20px', borderBottom: '1px solid #e8d4f8' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#722ed1' }}>
                                    <CarOutlined /> Chauffeurs directs
                                    <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>Affilié + Ambassadeur</Tag>
                                </div>
                            </div>
                            {getAmbassadorDirectMilestones(baremeThresholds).map((milestone, index) => (
                                <div
                                    key={`direct-${milestone.key}`}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 20px',
                                        borderBottom: '1px solid #f0f0f0',
                                        background: '#fefbff'
                                    }}
                                >
                                    <span style={{ color: '#595959', fontSize: 13 }}>{milestone.label}</span>
                                    <div style={{ textAlign: 'right' }}>
                                        <span style={{ fontWeight: 700, color: '#722ed1', fontSize: 14 }}>
                                            +{milestone.amount.toLocaleString()} F
                                        </span>
                                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                            ({milestone.affiliateAmount} + {milestone.ambassadorAmount})
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div style={{
                                padding: '10px 20px',
                                background: '#f3e8ff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: 600,
                                borderBottom: '1px solid #e8d4f8'
                            }}>
                                <span style={{ fontSize: 13 }}>Total direct</span>
                                <span style={{ color: '#722ed1' }}>{getTotalPotentialDirect().toLocaleString()} F</span>
                            </div>

                            {/* Barème chauffeurs indirects */}
                            <div style={{ background: '#e6f7ff', padding: '12px 20px', borderBottom: '1px solid #91d5ff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#1890ff' }}>
                                    <TeamOutlined /> Via affiliés
                                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>Ambassadeur seul</Tag>
                                </div>
                            </div>
                            {getAmbassadorMilestones(baremeThresholds).map((milestone, index) => (
                                <div
                                    key={`indirect-${milestone.key}`}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 20px',
                                        borderBottom: index < getAmbassadorMilestones(baremeThresholds).length - 1 ? '1px solid #f0f0f0' : 'none',
                                        background: '#fafeff'
                                    }}
                                >
                                    <span style={{ color: '#595959', fontSize: 13 }}>{milestone.label}</span>
                                    <span style={{ fontWeight: 700, color: '#52c41a', fontSize: 14 }}>
                                        +{milestone.amount.toLocaleString()} F
                                    </span>
                                </div>
                            ))}
                            <div style={{
                                padding: '10px 20px',
                                background: '#e6f7ff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: 600
                            }}>
                                <span style={{ fontSize: 13 }}>Total indirect</span>
                                <span style={{ color: '#1890ff' }}>{getTotalPotentialIndirect().toLocaleString()} F</span>
                            </div>
                        </Card>
                    ) : (
                        // Pour les affiliés, afficher le barème (simple ou double si mixte)
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    <span style={{ fontWeight: 600 }}>Barème des commissions</span>
                                    {cityOptions.length >= 1 && (
                                        <Select
                                            placeholder="Ville"
                                            value={selectedCity}
                                            onChange={(value) => setSelectedCity(value)}
                                            allowClear
                                            size="small"
                                            style={{ minWidth: 120 }}
                                            options={cityOptions}
                                        />
                                    )}
                                </div>
                            }
                            style={{ borderRadius: 12, marginBottom: 20 }}
                            bodyStyle={{ padding: 0 }}
                        >
                            {showBothBaremes ? (
                                // Afficher les deux barèmes si chauffeurs mixtes
                                <>
                                    {/* Barème Nouveaux chauffeurs */}
                                    <div style={{ background: '#e6f7ff', padding: '10px 20px', borderBottom: '1px solid #91d5ff' }}>
                                        <div style={{ fontWeight: 600, color: '#1890ff', fontSize: 13 }}>
                                            <CarOutlined style={{ marginRight: 8 }} />Nouveaux chauffeurs
                                        </div>
                                    </div>
                                    {getAffiliateMilestones(baremeThresholds).map((milestone, index) => (
                                        <div key={`new-${milestone.key}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #f0f0f0', background: '#fafeff' }}>
                                            <span style={{ color: '#595959', fontSize: 13 }}>{milestone.label}</span>
                                            <span style={{ fontWeight: 700, color: '#52c41a', fontSize: 13 }}>+{milestone.amount.toLocaleString()} F</span>
                                        </div>
                                    ))}
                                    <div style={{ padding: '10px 20px', background: '#e6f7ff', display: 'flex', justifyContent: 'space-between', fontWeight: 600, borderBottom: '1px solid #91d5ff' }}>
                                        <span style={{ fontSize: 13 }}>Total nouveau</span>
                                        <span style={{ color: '#1890ff' }}>{getTotalPotential(false, false).toLocaleString()} F</span>
                                    </div>
                                    {/* Barème Réactivation */}
                                    <div style={{ background: '#fff7e6', padding: '10px 20px', borderBottom: '1px solid #ffd591' }}>
                                        <div style={{ fontWeight: 600, color: '#fa8c16', fontSize: 13 }}>
                                            <CarOutlined style={{ marginRight: 8 }} />Réactivation
                                        </div>
                                    </div>
                                    {getAffiliateReactivationMilestones(baremeRates).map((milestone, index) => (
                                        <div key={`react-${milestone.key}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: index < 2 ? '1px solid #f0f0f0' : 'none', background: '#fffbf0' }}>
                                            <span style={{ color: '#595959', fontSize: 13 }}>{milestone.label}</span>
                                            <span style={{ fontWeight: 700, color: '#52c41a', fontSize: 13 }}>+{milestone.amount.toLocaleString()} F</span>
                                        </div>
                                    ))}
                                    <div style={{ padding: '10px 20px', background: '#fff7e6', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                                        <span style={{ fontSize: 13 }}>Total réactivation</span>
                                        <span style={{ color: '#fa8c16' }}>{getTotalPotential(false, true).toLocaleString()} F</span>
                                    </div>
                                </>
                            ) : (
                                // Barème simple (nouveau ou réactivation selon les chauffeurs)
                                <>
                                    {showReactivationBareme && (
                                        <div style={{ background: '#fff7e6', padding: '10px 20px', borderBottom: '1px solid #ffd591' }}>
                                            <div style={{ fontWeight: 600, color: '#fa8c16', fontSize: 13 }}>
                                                <CarOutlined style={{ marginRight: 8 }} />Barème Réactivation
                                            </div>
                                        </div>
                                    )}
                                    {milestones.map((milestone, index) => (
                                        <div
                                            key={milestone.key}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '14px 20px',
                                                borderBottom: index < milestones.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                background: index % 2 === 0 ? '#fafafa' : '#fff'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <div style={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    background: (showReactivationBareme ? '#fa8c16' : themeColor) + '15',
                                                    color: showReactivationBareme ? '#fa8c16' : themeColor,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 600,
                                                    fontSize: 12
                                                }}>
                                                    {index + 1}
                                                </div>
                                                <span style={{ color: '#595959' }}>{milestone.label}</span>
                                            </div>
                                            <span style={{ fontWeight: 700, color: '#52c41a' }}>
                                                +{milestone.amount.toLocaleString()} F
                                            </span>
                                        </div>
                                    ))}
                                    <div style={{
                                        padding: '14px 20px',
                                        background: (showReactivationBareme ? '#fa8c16' : themeColor) + '10',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontWeight: 600
                                    }}>
                                        <span>Total potentiel</span>
                                        <span style={{ color: showReactivationBareme ? '#fa8c16' : themeColor }}>{totalPotential.toLocaleString()} F</span>
                                    </div>
                                </>
                            )}
                        </Card>
                    )}

                </Col>

                {/* Colonne droite - Liste chauffeurs */}
                <Col xs={24} lg={16}>
                    {isAmbassador ? renderAmbassadorView() : renderAffiliateView()}
                </Col>
            </Row>

        </div>
    );
};

export default CommissionDetailPage;
